import React from 'react';
import BackHeader from '../components/BackHeader';
import useTranslation from '../hooks/useTranslation';

const TermsOfServicePage: React.FC = () => {
  const { t } = useTranslation();
  
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="bg-white min-h-screen flex flex-col">
      <BackHeader title={t('Terms of Use')} />
      
      <div className="p-4 pb-20 flex-1 overflow-y-auto">
        <section className="mb-6 text-left">
          <p className="text-gray-700 leading-relaxed">
            These Terms of Use ("Agreement") set forth the terms and conditions under which <strong>ORDER MADE EASY</strong> ("Company") provides a delivery order agency service for foreign tourists (the "Service"). This Agreement defines the rights and responsibilities between the Company and users of the Service.
          </p>
        </section>
        
        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 1 (Purpose)</h3>
          <p className="text-gray-700 leading-relaxed">
            This Agreement aims to define the terms of service, rights, duties, and responsibilities between the Company and Users with respect to the use of the delivery order agency service provided to foreign tourists.
          </p>
        </section>

        <hr className="mb-6 border-gray-200" />
        
        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 2 (Definitions)</h3>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>"User" refers to a foreign tourist who uses the Company's Service.</li>
            <li>"Service" refers to the Company's activity of placing food orders to restaurants on behalf of Users and coordinating the delivery.</li>
            <li>"Order Confirmation" means the point at which the User completes payment and the Company begins processing the order.</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />
        
        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 3 (Effect and Changes to the Agreement)</h3>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>This Agreement becomes effective upon being posted on the Company's website or service screen.</li>
            <li>The Company may amend this Agreement to the extent permitted by applicable law and will provide prior notice of such changes.</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 4 (Provision of Services)</h3>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>The Service is available exclusively within <strong>Seoul, South Korea</strong>, and only to <strong>foreign tourists.</strong></li>
            <li>Users are not required to create an account, but must provide a valid <strong>email address at the time of payment.</strong></li>
            <li>The email is used for order status updates and delivery tracking notifications.</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 5 (Order and Payment)</h3>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>Users place an order by selecting menu items and completing payment.</li>
            <li>Payments are securely processed through a third-party payment gateway (PG), <strong>Eximbay</strong>, designated by the Company.</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 6 (Order Confirmation and Contract Formation)</h3>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>A legally binding contract is formed when the User completes payment, after which the Company immediately begins processing the order.</li>
            <li><strong>Once the order is confirmed, cancellations or refunds are not permitted.</strong></li>
            <li>However, if the order cannot be fulfilled due to <strong>restaurant issues (e.g. sold out, unavailable menu), the User will receive a full refund.</strong></li>
          </ul>
        </section>
        
        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 7 (Refunds and Limitation of Liability)</h3>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>The Company is not responsible for issues related to food quality, allergies, or delivery delays caused by restaurants or delivery partners.</li>
            <li>In the event that an order is cancelled due to restaurant-side issues (e.g. out of stock), the Company will refund the full payment.</li>
            <li>The Company shall not be liable for damages unless caused by willful misconduct or gross negligence.</li>
            <li>To minimize chargeback risks, the Company prioritizes active communication with Users and provides in-house compensation or partial refunds when necessary.</li>
            <li>The Company proactively addresses customer complaints through email and other communication channels, and aims to resolve issues before they escalate into chargebacks.</li>
            <li>The Company also provides pre-order notices regarding allergies, food quality, and potential delivery delays on the order screen and FAQ page.</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 8 (Personal Information Protection)</h3>
          <p className="text-gray-700 leading-relaxed">
            User personal information is handled in accordance with applicable laws and the Company's Privacy Policy.
          </p>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 9 (Marketing and Promotions)</h3>
          <p className="text-gray-700 leading-relaxed">
            With the User's prior consent, the Company may send promotional emails, discount coupons, or event notifications. Users may opt out at any time.
          </p>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 10 (User Responsibilities)</h3>
          <p className="text-gray-700 leading-relaxed mb-2">Users shall not:</p>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>Submit false or inaccurate information</li>
            <li>Impersonate others or misuse third-party information</li>
            <li>Disrupt or interfere with the operation of the Service</li>
            <li>Violate any applicable laws or regulations of the Republic of Korea</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 11 (Company Disclaimers)</h3>
          <p className="text-gray-700 leading-relaxed mb-2">The Company shall not be liable for:</p>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>Damages caused by the User's negligence or error</li>
            <li>Issues arising from the restaurant (e.g., quality, delays, unavailable menu)</li>
            <li>Temporary suspension due to PG, system, or external service failures</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 12 (Dispute Resolution and Jurisdiction)</h3>
          <p className="text-gray-700 leading-relaxed">
            In the event of any dispute, the matter shall be resolved under the jurisdiction of the courts of the Republic of Korea, where the Company is located.
          </p>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 13 (Electronic Contract Formation)</h3>
          <p className="text-gray-700 leading-relaxed">
            Even without account registration, a valid electronic contract is deemed formed under the Electronic Commerce Act when the User submits an order and completes payment.
          </p>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Article 14 (Language and Interpretation)</h3>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>The Service may provide content in multiple languages for convenience, but all legal matters shall be governed by the laws of the Republic of Korea and the <strong>Korean version of this Agreement.</strong></li>
            <li>In the event of discrepancies between translations and the Korean version, the <strong>Korean version shall prevail.</strong></li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
